import { Component, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CreateTicketData } from '../models/create-ticket.model';
@Component({
  selector: 'app-ticket-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  template: `
    <div class="max-w-3xl mx-auto bg-white rounded-lg shadow-lg p-4 sm:p-8">
      <div class="mb-8 text-center">
        <h1 class="text-lg sm:text-2xl font-bold text-vodafone-red">Support Portal</h1>
        <p class="text-gray-600">Submit a new support ticket</p>
      </div>
      <form [formGroup]="ticketForm" (ngSubmit)="onSubmit()" class="space-y-3 sm:space-y-6">
        <div>
          <label for="title" class="block text-sm font-medium text-gray-700">Summary *</label>
          <input
            type="text"
            id="title"
            formControlName="title"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-vodafone-red focus:ring-vodafone-red sm:text-sm"
            placeholder="Brief summary of the issue"
          />
          <p
            *ngIf="
              ticketForm.get('title')?.touched && ticketForm.get('title')?.errors?.['required']
            "
            class="mt-1 text-sm text-red-600"
          >
            Summary is required
          </p>
        </div>
        <div>
          <label for="description" class="block text-sm font-medium text-gray-700">Description *</label>
          <textarea
            id="description"
            formControlName="description"
            rows="4"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-vodafone-red focus:ring-vodafone-red sm:text-sm"
            placeholder="Please provide detailed information about your issue"
          ></textarea>
          <p
            *ngIf="
              ticketForm.get('description')?.touched &&
              ticketForm.get('description')?.errors?.['required']
            "
            class="mt-1 text-sm text-red-600"
          >
            Description is required
          </p>
        </div>
        <div>
          <label for="hostname" class="block text-sm font-medium text-gray-700">Hostname Label (optional)</label>
          <input
            type="text"
            id="hostname"
            formControlName="hostname"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-vodafone-red focus:ring-vodafone-red sm:text-sm"
            placeholder="e.g., E0xxxxxx"
          />
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label for="impactLevel" class="block text-sm font-medium text-gray-700">Impact Level *</label>
            <select
              id="impactLevel"
              formControlName="impactLevel"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-vodafone-red focus:ring-vodafone-red sm:text-sm"
            >
              <option value="">Select Impact Level</option>
              <option value="none">None</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <div>
            <label for="urgency" class="block text-sm font-medium text-gray-700">Urgency *</label>
            <select
              id="urgency"
              formControlName="urgency"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-vodafone-red focus:ring-vodafone-red sm:text-sm"
            >
              <option value="">Select Urgency</option>
              <option value="none">None</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
        </div>
        <div>
          <label for="priority" class="block text-sm font-medium text-gray-700">Priority *</label>
          <select
            id="priority"
            formControlName="priority"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-vodafone-red focus:ring-vodafone-red sm:text-sm"
          >
            <option value="">Select Priority</option>
            <option value="P1">P1 - Critical</option>
            <option value="P2">P2 - High</option>
            <option value="P3">P3 - Medium</option>
            <option value="P4">P4 - Normal</option>
          </select>
        </div>
        <div>
          <label for="attachments" class="block text-sm font-medium text-gray-700">Attachments (optional)</label>
          <input
            type="file"
            id="attachments"
            formControlName="attachments"
            multiple
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-vodafone-red focus:ring-vodafone-red sm:text-sm"
          />
        </div>
        <div class="flex justify-end">
          <button
            type="submit"
            [disabled]="ticketForm.invalid"
            class="inline-flex justify-center py-1.5 px-4 sm:py-2 sm:px-6 border border-transparent shadow-sm text-xs sm:text-sm font-medium rounded-md text-white bg-vodafone-red hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-vodafone-red disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Submit Ticket
          </button>
        </div>
      </form>
    </div>
  `,
})
export class TicketFormComponent {
  @Output() submitTicket = new EventEmitter<CreateTicketData>();
  ticketForm: FormGroup;

  constructor(private fb: FormBuilder) {
    this.ticketForm = this.fb.group({
      title: ['', Validators.required],
      description: ['', Validators.required],
      hostname: [''],
      impactLevel: ['', Validators.required],
      urgency: ['', Validators.required],
      priority: ['', Validators.required],
      attachments: [''],
    });
  }

  onSubmit(): void {
    if (this.ticketForm.valid) {
      this.submitTicket.emit(this.ticketForm.value);
    }
  }

  reset(): void {
    this.ticketForm.reset();
  }
}
